import {
  Alert,
  Button,
  Breadcrumb,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { CopyOutlined, HomeOutlined } from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { openNotification } from "../../../components/Notification";
import PageHeader from "../../../components/PageHeader";
import { UserContext } from "../../../contexts/UserContext";
import { integracaoDenunciaService, montarUrlIntegracaoDenuncia } from "../../../services/resources/integracaoDenunciaService";
import { podeGerenciarIntegracaoDenuncia, urls } from "../../../utils";

const { TabPane } = Tabs;

const cardStyle = {
  border: "1px solid var(--border-color)",
  borderRadius: 8,
  marginBottom: 16,
};

/**
 * Checkbox.Group dentro de Tabs perde valores das abas inativas.
 * Cada aba tem o próprio Group; o valor do Form une todas as seleções.
 */
const SelecaoCamposIntegracao = ({ value = [], onChange, camposPorGrupo, disabled }) => {
  const selecionados = Array.isArray(value) ? value : [];

  const alterarGrupo = (valoresDoGrupo, camposDoGrupo) => {
    const codigosDoGrupo = new Set(camposDoGrupo.map((campo) => campo.codigo));
    const foraDoGrupo = selecionados.filter((codigo) => !codigosDoGrupo.has(codigo));
    onChange?.([...foraDoGrupo, ...valoresDoGrupo]);
  };

  return (
    <Tabs>
      {Object.entries(camposPorGrupo).map(([grupo, campos]) => {
        const valorDaAba = selecionados.filter((codigo) =>
          campos.some((campo) => campo.codigo === codigo)
        );
        return (
          <TabPane tab={grupo} key={grupo}>
            <Checkbox.Group
              style={{ width: "100%" }}
              value={valorDaAba}
              disabled={disabled}
              onChange={(valores) => alterarGrupo(valores, campos)}
            >
              <Row gutter={[12, 12]}>
                {campos.map((campo) => (
                  <Col xs={24} sm={12} xl={8} key={campo.codigo}>
                    <div className="campo-integracao-check">
                      <Checkbox value={campo.codigo}>
                        {campo.rotulo}
                        {campo.sensivel ? (
                          <Tag color="orange" style={{ marginLeft: 8 }}>
                            sensível
                          </Tag>
                        ) : null}
                      </Checkbox>
                    </div>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </TabPane>
        );
      })}
    </Tabs>
  );
};

export const FormularioIntegracaoDenuncia = ({ onCreate, onEdit, onView }) => {
  const [form] = Form.useForm();
  const history = useHistory();
  const { codigo } = useParams();
  const { user } = useContext(UserContext);
  const autorizado = podeGerenciarIntegracaoDenuncia(user);

  const [loading, setLoading] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [canais, setCanais] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [chaveGerada, setChaveGerada] = useState(null);
  const [chaveCopiada, setChaveCopiada] = useState(false);
  const [urlCopiada, setUrlCopiada] = useState(false);
  const [urlEndpoint, setUrlEndpoint] = useState("");
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState({ currentPage: 0, totalElements: 0, size: 20 });

  const tipoEscopo = Form.useWatch("tipoEscopo", form);
  const abrangenciaGrupo = Form.useWatch("abrangenciaGrupo", form);

  useEffect(() => {
    if (!autorizado) return;
    carregarApoio();
    if (codigo) carregarIntegracao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autorizado, codigo]);

  async function carregarApoio() {
    try {
      const [{ data: campos }, { data: canaisData }, { data: gruposData }] =
        await Promise.all([
          integracaoDenunciaService.catalogoCampos(),
          integracaoDenunciaService.listarCanais({ page: 0, size: 20 }),
          integracaoDenunciaService.listarGrupos({ page: 0, size: 20 }),
        ]);
      setCatalogo(campos || []);
      setCanais(canaisData.content || []);
      setGrupos(gruposData.content || []);
      if (onCreate) {
        form.setFieldsValue({
          tipoEscopo: "CANAL",
          ativa: true,
          camposPermitidos: (campos || [])
            .filter((c) => c.padrao)
            .map((c) => c.codigo),
        });
      }
    } catch (error) {
      openNotification("error", <strong>Erro ao carregar dados</strong>,
        error?.response?.data?.userMessage);
    }
  }

  async function buscarCanais(nome) {
    const { data } = await integracaoDenunciaService.listarCanais({
      nome,
      page: 0,
      size: 20,
    });
    setCanais(data.content || []);
  }

  async function buscarGrupos(nome) {
    const { data } = await integracaoDenunciaService.listarGrupos({
      nome,
      page: 0,
      size: 20,
    });
    setGrupos(data.content || []);
  }

  async function carregarIntegracao() {
    setLoading(true);
    try {
      const { data } = await integracaoDenunciaService.get(codigo);
      setUrlEndpoint(montarUrlIntegracaoDenuncia(data.identificadorEndpoint));
      form.setFieldsValue({
        nome: data.nome,
        descricao: data.descricao,
        tipoEscopo: data.tipoEscopo,
        codigoCanal: data.codigoCanal,
        abrangenciaGrupo: data.abrangenciaGrupo,
        codigoGrupo: data.codigoGrupo,
        camposPermitidos: data.camposPermitidos,
        ativa: data.ativa,
      });
      if (onView || onEdit) {
        carregarLogs(0);
      }
    } catch (error) {
      openNotification("error", <strong>Erro ao carregar integração</strong>,
        error?.response?.data?.userMessage);
    } finally {
      setLoading(false);
    }
  }

  async function carregarLogs(page) {
    const { data } = await integracaoDenunciaService.listarLogs(codigo, {
      page,
      size: 20,
    });
    setLogs(data.content || []);
    setLogsPage({
      currentPage: data.currentPage,
      totalElements: data.totalElements,
      size: data.size,
    });
  }

  function montarPayload(values) {
    return {
      nome: values.nome,
      descricao: values.descricao,
      tipoEscopo: values.tipoEscopo,
      codigoCanal: values.tipoEscopo === "CANAL" ? values.codigoCanal : null,
      abrangenciaGrupo: values.tipoEscopo === "GRUPO" ? values.abrangenciaGrupo : null,
      codigoGrupo:
        values.tipoEscopo === "GRUPO" && values.abrangenciaGrupo === "GRUPO_ESPECIFICO"
          ? values.codigoGrupo
          : null,
      camposPermitidos: values.camposPermitidos,
      ativa: values.ativa,
      confirmarAcessoTotal:
        values.tipoEscopo === "GRUPO" && values.abrangenciaGrupo === "ACESSO_TOTAL",
    };
  }

  async function salvar() {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload = montarPayload(values);
      if (onCreate) {
        const { data } = await integracaoDenunciaService.create(payload);
        setChaveGerada(data);
        setUrlEndpoint(
          montarUrlIntegracaoDenuncia(data?.integracao?.identificadorEndpoint)
        );
        openNotification("success", <strong>Integração criada</strong>);
      } else {
        await integracaoDenunciaService.update(codigo, payload);
        openNotification("success", <strong>Integração atualizada</strong>);
        history.push(urls.INTEGRACOES_DENUNCIA_LIST);
      }
    } catch (error) {
      if (error?.errorFields) return;
      openNotification("error", <strong>Não foi possível salvar</strong>,
        error?.response?.data?.userMessage || error?.response?.data?.detail);
    } finally {
      setLoading(false);
    }
  }

  async function copiarUrl() {
    if (!urlEndpoint) return;
    try {
      await navigator.clipboard.writeText(urlEndpoint);
      setUrlCopiada(true);
    } catch (error) {
      openNotification(
        "error",
        <strong>Não foi possível copiar a URL</strong>,
        "Copie o texto manualmente."
      );
    }
  }

  async function copiarChave() {
    const chave = chaveGerada?.chaveApi;
    if (!chave) return;
    try {
      await navigator.clipboard.writeText(chave);
      setChaveCopiada(true);
    } catch (error) {
      openNotification(
        "error",
        <strong>Não foi possível copiar a chave</strong>,
        "Copie o texto manualmente."
      );
    }
  }

  async function regenerar() {
    try {
      const { data } = await integracaoDenunciaService.regenerarChave(codigo);
      setChaveGerada(data);
      openNotification("success", <strong>Nova chave gerada</strong>);
    } catch (error) {
      openNotification("error", <strong>Falha ao regenerar chave</strong>,
        error?.response?.data?.userMessage);
    }
  }

  const camposPorGrupo = useMemo(() => {
    return catalogo.reduce((acc, campo) => {
      acc[campo.grupo] = acc[campo.grupo] || [];
      acc[campo.grupo].push(campo);
      return acc;
    }, {});
  }, [catalogo]);

  if (!autorizado) {
    return <Alert type="error" message="Acesso restrito ao grupo OISOL MASTER" />;
  }

  const titulo = onCreate
    ? "NOVA INTEGRAÇÃO"
    : onEdit
    ? "EDITAR INTEGRAÇÃO"
    : "DETALHE DA INTEGRAÇÃO";

  return (
    <div id="sectionMain">
      <Breadcrumb id="breadcrumb">
        <Breadcrumb.Item>
          <Link to="">
            <HomeOutlined />
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={urls.INTEGRACOES_DENUNCIA_LIST}>integrações de denúncias</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <strong>{titulo.toLowerCase()}</strong>
        </Breadcrumb.Item>
      </Breadcrumb>
      <div className="container-section">
        <PageHeader
          titulo={titulo}
          subtitulo="Configure o escopo, a situação e os campos que o sistema externo poderá consultar."
        />
        <Spin spinning={loading}>
          <Form form={form} layout="vertical" disabled={onView}>
            <Card title="Dados da integração" size="small" style={cardStyle}>
              <Row gutter={[16, 0]}>
                <Col xs={24} md={16}>
                  <Form.Item name="nome" label="Nome" rules={[{ required: true }]}>
                    <Input maxLength={120} placeholder="Ex.: integração MPCE" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="ativa" label="Situação" valuePropName="checked">
                    <Switch checkedChildren="ativa" unCheckedChildren="inativa" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="descricao" label="Descrição">
                    <Input.TextArea rows={3} maxLength={500} placeholder="Uso interno da integração" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="Escopo" size="small" style={cardStyle}>
              <Form.Item name="tipoEscopo" label="Tipo de escopo" rules={[{ required: true }]}>
                <Radio.Group optionType="button" buttonStyle="solid">
                  <Radio.Button value="CANAL">Canal</Radio.Button>
                  <Radio.Button value="GRUPO">Grupo</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {tipoEscopo === "CANAL" && (
                <Form.Item name="codigoCanal" label="Canal" rules={[{ required: true }]}>
                  <Select
                    showSearch
                    filterOption={false}
                    onSearch={buscarCanais}
                    placeholder="Busque o canal"
                    options={canais.map((c) => ({ label: c.nome, value: c.codigo }))}
                  />
                </Form.Item>
              )}

              {tipoEscopo === "GRUPO" && (
                <>
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Acesso total retorna denúncias de qualquer grupo, respeitando os campos selecionados. O isolamento por grupo específico não está disponível: o modelo atual não vincula denúncia a grupo."
                  />
                  <Form.Item
                    name="abrangenciaGrupo"
                    label="Abrangência"
                    rules={[{ required: true }]}
                  >
                    <Radio.Group optionType="button" buttonStyle="solid">
                      <Radio.Button value="ACESSO_TOTAL">Acesso total</Radio.Button>
                      <Radio.Button value="GRUPO_ESPECIFICO" disabled>
                        Grupo específico (indisponível)
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </>
              )}

              {abrangenciaGrupo === "ACESSO_TOTAL" && (
                <Alert
                  type="error"
                  showIcon
                  message="Ao salvar, você confirma explicitamente a concessão de acesso total."
                />
              )}
            </Card>

            <Card title="Campos retornados" size="small" style={cardStyle}>
              <Form.Item
                name="camposPermitidos"
                rules={[{ required: true, message: "Selecione ao menos um campo" }]}
                style={{ marginBottom: 0 }}
              >
                <SelecaoCamposIntegracao camposPorGrupo={camposPorGrupo} />
              </Form.Item>
            </Card>
          </Form>

          {urlEndpoint && (
            <Alert
              style={{ marginBottom: 16 }}
              message="URL do endpoint"
              description={
                <Typography.Paragraph
                  style={{ marginBottom: 0 }}
                  copyable={{
                    text: urlEndpoint,
                    onCopy: copiarUrl,
                  }}
                >
                  {urlEndpoint}
                </Typography.Paragraph>
              }
            />
          )}

          {!onView && (
            <Row justify="end">
              <Space>
                <Button onClick={() => history.push(urls.INTEGRACOES_DENUNCIA_LIST)}>
                  Cancelar
                </Button>
                {onEdit && (
                  <Popconfirm
                    title="A chave anterior deixará de funcionar. Continuar?"
                    onConfirm={regenerar}
                  >
                    <Button>Regenerar chave</Button>
                  </Popconfirm>
                )}
                <Button type="primary" onClick={salvar}>
                  Salvar
                </Button>
              </Space>
            </Row>
          )}

          {onView && codigo && (
            <Table
              style={{ marginTop: 24 }}
              rowKey="codigo"
              title={() => "Logs de utilização"}
              dataSource={logs}
              columns={[
                { title: "data", dataIndex: "dataHora" },
                { title: "resultado", dataIndex: "resultado" },
                { title: "status", dataIndex: "statusHttp" },
                { title: "registros", dataIndex: "quantidadeRegistros" },
                { title: "página", dataIndex: "pagina" },
                { title: "motivo", dataIndex: "motivoFalha" },
              ]}
              pagination={{
                current: logsPage.currentPage + 1,
                total: logsPage.totalElements,
                pageSize: logsPage.size,
                onChange: (page) => carregarLogs(page - 1),
              }}
            />
          )}
        </Spin>
      </div>

      <Modal
        title="Chave de API gerada"
        visible={!!chaveGerada}
        maskClosable={false}
        closable
        onCancel={() => {
          setChaveGerada(null);
          history.push(urls.INTEGRACOES_DENUNCIA_LIST);
        }}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={copiarChave}>
            Copiar chave
          </Button>,
          <Button
            key="ok"
            type="primary"
            onClick={() => {
              setChaveGerada(null);
              history.push(urls.INTEGRACOES_DENUNCIA_LIST);
            }}
          >
            Entendi, já armazenei
          </Button>,
        ]}
      >
        <Alert
          type="warning"
          showIcon
          message="Guarde a chave em local seguro. Ela não poderá ser consultada novamente."
          style={{ marginBottom: 16 }}
        />
        <Typography.Paragraph
          copyable={{
            text: chaveGerada?.chaveApi,
            onCopy: copiarChave,
          }}
        >
          {chaveGerada?.chaveApi}
        </Typography.Paragraph>
      </Modal>

      <Modal
        title="Chave copiada"
        visible={chaveCopiada}
        maskClosable={false}
        onCancel={() => setChaveCopiada(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setChaveCopiada(false)}>
            Ok
          </Button>,
        ]}
      >
        A chave de API foi copiada para a área de transferência. Guarde-a em
        local seguro; ela não poderá ser consultada novamente.
      </Modal>

      <Modal
        title="URL copiada"
        visible={urlCopiada}
        maskClosable={false}
        onCancel={() => setUrlCopiada(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setUrlCopiada(false)}>
            Ok
          </Button>,
        ]}
      >
        A URL do endpoint foi copiada para a área de transferência.
      </Modal>
    </div>
  );
};
